<template>
  <div class="jps-mynote">
    <UiSideMenu width="18rem" :is-clicked="isClicked">
      <UiSideMenuList class="side-menu-item">
        <template #head="">
          <h3>
            {{ ls({ ja: 'マイノート', en: 'My Notes' }) }}
          </h3>
          <button @click="sortNote()">
            {{ ls({ ja: '並べ替え', en: 'Sort' }) }}
          </button>
        </template>
        <template #list="">
          <li v-for="(n, index) in noteStore.list" :key="n.id">
            <button
              class="mynote-list-item"
              :class="{ 'jps-active': index === 0 }"
              @click.stop="() => {
                editNote(n.id)
                isClicked = !isClicked
              }"
            >
              {{ ls(n.title) }}
            </button>
          </li>
        </template>
      </UiSideMenuList>
      <div class="side-menu-item">
        <BaseLink to="/mygallery">{{
          ls({ ja: 'マイギャラリー', en: 'My Gallery' })
        }}</BaseLink>
      </div>
      <div class="side-menu-item">
        <BaseLink to="/mysearch">{{
          ls({ ja: 'マイサーチ', en: 'My Search' })
        }}</BaseLink>
      </div>
    </UiSideMenu>
    <UiEditLayout class="mynote-main" id="jps-curation-editor">
      <template #tools="">
        <UiEditLayoutButton type="NEW_NOTE" @click="addNewNote()" />
        <UiEditLayoutButton type="COPY" @click="copyNote()" :disabled="!singleOpActive" />
        <UiEditLayoutButton type="DELETE" @click="deleteNote()" :disabled="!singleOpActive" />
        <UiEditLayoutButton type="IMPORT" @click="importNote()" :disabled="!singleOpActive" />
        <UiEditLayoutButton type="EXPORT" @click="exportNote()" :disabled="!singleOpActive" />
        <UiEditLayoutButton type="UNDO" @click="undo.undo()" v-if="editstate.undo" :disabled="!editstate.canUndo" :button-class="['is-right']" />
        <UiEditLayoutButton type="REDO" @click="undo.redo()" v-if="editstate.redo" :disabled="!editstate.canRedo" />
        <UiEditLayoutButton type="SAVE" @click="saveImpl()" :is-saving="isSaving" />
      </template>
      <div class="editor-body">
        <CurationEditor2 :editstate="editstate" />
      </div>
    </UiEditLayout>
    <UiInputModal ref="addNewNoteInputModal" :title="{ ja: '新しいノートのタイトル', en: 'Title for new note' }" />
    <UiSortModal ref="sortModal" :renderer="(f) => ls(f.title)" />
    <UiInputModal ref="copyNoteInputModal" :title="{ ja: 'コピー先のタイトル', en: 'Title for duplicated note' }" />
    <MynoteImportModal ref="mynoteImportModal" />
    <UiConfirmModal ref="confirmModal" />
  </div>
</template>

<script lang="ts" setup>
definePageMeta({
  disableFooter: true,
  disableDefaultMargin: true,
})
import { type CurationEditState, dapLangGlobal } from '#imports'
import type { UiConfirmModal, UiInputModal, UiSortModal, MynoteImportModal } from '#components'

const { getPageAndCache } = usePage()
const { meta } = await getPageAndCache()
useHead(meta)

const isClicked = ref(false)

const { ls, lst } = useI18n()
const { snackbarSuccess } = useSnackbar()
const noteStore = useNoteStore()

const isSaving: Ref<boolean> = ref(false)
const saveImpl = async () => {
  isSaving.value = true
  await noteStore.updateNote(undo.undoable.value)
  isSaving.value = false
  undo.waitSaving.value = false
}
const undo = useUndo({ saveImpl: saveImpl })

undo.autosaveEnabled.value = true
undo.undoable.value = null

const editstate = computed(() => {
  return {
    canUndo: undo.canUndo.value,
    canRedo: undo.canRedo.value,
    canSave: false,
    isSaving: false,
    curation: undo.undoable.value,
    page: 'mynote',
    mode: 'note',
    undo: undo.undo,
    redo: undo.redo,
    save: null,
    snapshot: undo.snapshot,
  } as CurationEditState
})

const editNote = async (id: string) => {
  if (undo.undoable.value && undo.undoable.value.id === id) return
  await noteStore.changePrimary(id)
  await loadPrimary()
}

onBeforeMount(() => {
  loadPrimary()
})

const loadPrimary = async () => {
  if (noteStore.primary) {
    if (undo.waitSaving.value) {
      await undo.forceSave()
    }
    undo.ignoreChange.value = true
    undo.undoable.value = noteStore.primary
    undo.resetHistory()
  } else {
    setTimeout(loadPrimary, 250)
  }
}

/////////////メニュー系
const addNewNoteInputModal = ref<InstanceType<typeof UiInputModal>>()
const addNewNote = async () => {
  try {
    const text = await addNewNoteInputModal.value.openAwait()
    if (!text) return
    let note = await noteStore.createNewNoteByTitle({ ja: text })
    await noteStore.changePrimary(note.id)
    await loadPrimary()
  } catch (cancel) {}
}

const sortModal = ref<InstanceType<typeof UiSortModal>>()
const sortNote = async () => {
  try {
    const sorted = await sortModal.value.openAwait(noteStore.list)
    if (!sorted) return
    noteStore.list = sorted
    await noteStore.changePrimary(sorted[0].id)
    await loadPrimary()
  } catch (cancel) {}
}

const copyNoteInputModal = ref<InstanceType<typeof UiInputModal>>()
const copyNote = async () => {
  try {
    const text = await copyNoteInputModal.value.openAwait()
    if (!text) return
    const copy = clone(noteStore.primary)
    copy.id = luId()
    copy.title = { ja: text }
    await noteStore.createNewNote(copy)
    await noteStore.changePrimary(copy.id)
    await loadPrimary()
    snackbarSuccess(
      {
        ja: text + 'をコピーしました',
        en: text + ' is coped',
      },
    )
  } catch (cancel) {}
}

const mynoteImportModal = ref<InstanceType<typeof MynoteImportModal>>()
const importNote = async () => {
  try {
    const curation = await mynoteImportModal.value.openAwait()
    if (!curation) return
    let note = noteStore.createNote(curation.title)
    if (curation?.parts?.[0].type === 'jps-curation-text') {
      note.parts[0] = curation.parts[0]
    }

    for (let i = 1; i < curation?.parts?.length; i++) {
      if (curation?.parts?.[i].type === 'jps-curation-list') {
        note.parts[i] = curation.parts[i]
      }
    }
    const r = await noteStore.createNewNote(note)
    await noteStore.changePrimary(r.id)
    await loadPrimary()
    snackbarSuccess(
      {
        ja: ls(curation.title) + 'をインポートしました',
        en: ls(curation.title) + ' is imported',
      },
    )
  } catch (cancel) {}
}

const exportNote = async () => {
  try {
    let c: Curation = clone(undo.undoable.value)
    delete c.edit
    delete c.permission
    delete c.theme
    exportJSON(c, ls(c.title) + '.json')
  } catch (cancel) {}
}

const singleOpActive = computed(() => {
  return noteStore.primary != null
})

const confirmModal = ref<InstanceType<typeof UiConfirmModal>>()
const deleteNote = async () => {
  try {
    const confirm = await confirmModal.value.openAwait({
      message: dapLangGlobal.deleteConfirm,
      messageVars: [ls(noteStore.primary?.title)]
    })
    if (!confirm) return
    const title = noteStore.primary.title
    await noteStore.deleteNote(noteStore.primary.id)
    await loadPrimary()
    snackbarSuccess({ ja: lst(dapLangGlobal.deleteSuccess, ls(title)) })
  } catch (cancel) {}
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.jps-mynote {
  height: $partial-scroll-height;
  display: flex;
  background-color: $jps-gray-50;

  .mynote-main {
    position: relative;
    flex-grow: 1;
    height: 100%;
    overflow-y: auto;

    .toolbar-container {
      position: sticky;
      left: 0;
      top: 0;
      z-index: 99999;
      width: 100%;
      .curation-editor-toolbar {
        position: absolute;
        right: 0;
        top: 0;
        border-bottom-left-radius: $global-radius-small;
      }
    }
    .editor-body {
      border-top-left-radius: $global-radius;
      border-top-right-radius: $global-radius;
      overflow: hidden;
      max-width: $max-content-width;
    }
  }
}
</style>
